import React, { useState } from "react";
import { connect } from "react-redux";
import { Dropdown } from "react-bootstrap";
import { FiMoreHorizontal } from "react-icons/fi";
import { authService, documentsService } from "services";
import { showModal, hideModal } from "actions/modalActions";
import { toastr } from "react-redux-toastr";
import DocumentMoveModal from "./DocumentMoveModal";
import DocumentRenameModal from "./DocumentRenameModal";
import DocumentSignContent from "./DocumentSignContent";
import { isSignableDocument, getFormatErrorMessage, isHaddockEnvironment } from "utils/documentUtils";
import { isDocumentFolderDebugEnabled, logFolderIdResolution } from "utils/documentFolderDebug";
import { hasSubscriptionFeature } from "utils/utils";
import UpgradeModal from "components/Modal/UpgradeModal";
import { useTranslation } from "react-i18next";

export function getStoreFolderIdFromAttachment(attachment) {
	if (!attachment) return null;
	const id =
		attachment.store_folder_id ??
		attachment.folder_id ??
		attachment.storeFolderId ??
		attachment.folderId ??
		attachment.store_folder?.id ??
		attachment.storeFolder?.id ??
		attachment.folder?.id;
	if (id != null && id !== "") {
		return id;
	}
	return null;
}

/** Slug de ruta de “pendientes de firma”: nunca es un id real de store-folder. */
const PENDING_SIGN_ROUTE_SLUG = "pending-signatures";

function matchFolderIdByName(attachment, foldersList) {
	if (!attachment?.folder_name || !Array.isArray(foldersList) || foldersList.length === 0) {
		return undefined;
	}
	const match = foldersList.find((f) => f.name === attachment.folder_name);
	return match?.id;
}

/**
 * Id del store-folder para acciones (descarga, firmar…): primero el adjunto, luego nombre de carpeta
 * frente a la lista de carpetas (documentos recientes), luego la carpeta abierta en la vista tabla.
 */
export function resolveStoreFolderIdForAttachment(
	attachment,
	fallbackStoreFolderId,
	foldersList,
	debugContext
) {
	const fromAtt = getStoreFolderIdFromAttachment(attachment);
	if (fromAtt != null && fromAtt !== "") {
		if (debugContext) {
			logFolderIdResolution(debugContext, attachment, {
				source: "attachment",
				fromAttachment: fromAtt,
				fromFolderName: undefined,
				fallbackStoreFolderId,
				foldersListCount: foldersList?.length ?? 0,
				resolvedFolderId: fromAtt,
			});
		}
		return fromAtt;
	}
	const fromName = matchFolderIdByName(attachment, foldersList);
	if (fromName != null && fromName !== "") {
		if (debugContext) {
			logFolderIdResolution(debugContext, attachment, {
				source: "folder_name_match",
				fromAttachment: null,
				fromFolderName: fromName,
				matchedFolderName: attachment?.folder_name,
				fallbackStoreFolderId,
				foldersListCount: foldersList?.length ?? 0,
				resolvedFolderId: fromName,
			});
		}
		return fromName;
	}
	if (fallbackStoreFolderId == null || fallbackStoreFolderId === "") {
		if (debugContext) {
			logFolderIdResolution(debugContext, attachment, {
				source: "none",
				fromAttachment: null,
				fromFolderName: undefined,
				fallbackStoreFolderId,
				foldersListCount: foldersList?.length ?? 0,
				resolvedFolderId: null,
				reason: "sin folderId en adjunto, sin match por nombre y sin fallback",
			});
		}
		return null;
	}
	if (String(fallbackStoreFolderId) === PENDING_SIGN_ROUTE_SLUG) {
		if (debugContext) {
			logFolderIdResolution(debugContext, attachment, {
				source: "none",
				fromAttachment: null,
				fromFolderName: undefined,
				fallbackStoreFolderId,
				foldersListCount: foldersList?.length ?? 0,
				resolvedFolderId: null,
				reason: "fallback es el slug de pendientes, no un store-folder real",
			});
		}
		return null;
	}
	if (debugContext) {
		logFolderIdResolution(debugContext, attachment, {
			source: "fallback",
			fromAttachment: null,
			fromFolderName: undefined,
			fallbackStoreFolderId,
			foldersListCount: foldersList?.length ?? 0,
			resolvedFolderId: fallbackStoreFolderId,
		});
	}
	return fallbackStoreFolderId;
}

/**
 * Localiza la carpeta de un adjunto recorriendo store-folders cuando la API de pendientes
 * no incluye store_folder_id (workaround hasta que el backend lo devuelva).
 */
export async function probeStoreFolderIdForAttachment(attachment, foldersList, employeeId) {
	const existing = getStoreFolderIdFromAttachment(attachment);
	if (existing) return existing;
	if (!attachment?.url || !Array.isArray(foldersList) || foldersList.length === 0) {
		return null;
	}

	for (const folder of foldersList.filter((f) => f.id)) {
		try {
			const response = await documentsService.getAttachments(folder.id, employeeId ?? null);
			const attachments = response?.data || response || [];
			if (
				Array.isArray(attachments) &&
				attachments.some((item) => item.url === attachment.url)
			) {
				return folder.id;
			}
		} catch (_) {}
	}

	return null;
}

export async function enrichAttachmentsWithStoreFolderIds(attachments, foldersList, employeeId) {
	if (!Array.isArray(attachments) || attachments.length === 0) {
		return attachments;
	}

	return Promise.all(
		attachments.map(async (attachment) => {
			if (getStoreFolderIdFromAttachment(attachment)) {
				return attachment;
			}
			const folderId = await probeStoreFolderIdForAttachment(
				attachment,
				foldersList,
				employeeId
			);
			if (!folderId) {
				return attachment;
			}
			const folder = foldersList.find((f) => f.id === folderId);
			return {
				...attachment,
				store_folder_id: folderId,
				folder_name: attachment.folder_name || folder?.name,
			};
		})
	);
}

export async function downloadStoreFolderAttachment(attachment, fallbackStoreFolderId, foldersList, t) {
	const authHeader = { Authorization: `Bearer ${authService.token.access_token}` };

	const folderId = resolveStoreFolderIdForAttachment(attachment, fallbackStoreFolderId, foldersList);

	if (folderId) {
		// Caso rápido: carpeta conocida, descarga directa
		try {
			const response = await fetch(
				`${global.BASE_API}store-folders/${folderId}/attachments/${attachment.url}`,
				{ method: "GET", headers: authHeader }
			);
			if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
			triggerBlobDownload(await response.blob(), attachment.name);
		} catch (error) {
			console.error("Error al descargar el archivo:", error);
			toastr.error("Error", t("documents.errorDownload"));
		}
		return;
	}

	// Caso lento: carpeta desconocida — prueba cada carpeta de la lista hasta encontrar el archivo
	const candidates = Array.isArray(foldersList) ? foldersList.filter((f) => f.id) : [];
	for (const folder of candidates) {
		try {
			const response = await fetch(
				`${global.BASE_API}store-folders/${folder.id}/attachments/${attachment.url}`,
				{ method: "GET", headers: authHeader }
			);
			if (response.ok) {
				triggerBlobDownload(await response.blob(), attachment.name);
				return;
			}
		} catch (_) {}
	}

	toastr.error("Error", t("documents.errorDeterminingFolder"));
}

function triggerBlobDownload(blob, filename) {
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename || "documento";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
}

/** Mantiene el menú visible en móvil (tablas con scroll, borde de pantalla). */
const ACTIONS_MENU_POPPER_CONFIG = {
	strategy: "fixed",
	modifiers: [
		{ name: "offset", options: { offset: [0, 6] } },
		{
			name: "preventOverflow",
			options: {
				boundary: "viewport",
				padding: 8,
				altAxis: true,
			},
		},
		{
			name: "flip",
			options: {
				fallbackPlacements: ["bottom-end", "bottom-start", "top-end", "top-start"],
				padding: 8,
			},
		},
	],
};

const DocumentAttachmentActionsMenu = ({
	attachment,
	user,
	showModal: dispatchShowModal,
	hideModal: dispatchHideModal,
	onAfterMutation,
	className,
	fallbackStoreFolderId,
	foldersList,
	debugContext,
}) => {
	const { t } = useTranslation();
	const [showUpgradeModal, setShowUpgradeModal] = useState(false);
	const [upgradeFeature, setUpgradeFeature] = useState("");

	const closeModal = () => dispatchHideModal();

	const folderId = resolveStoreFolderIdForAttachment(
		attachment,
		fallbackStoreFolderId,
		foldersList,
		debugContext
	);
	const isAuthor = attachment.creator && user && attachment.creator.id === user.id;
	const isAdmin = user && user.roles && user.roles.includes("ADMIN");
	const canDelete = isAuthor || (user && user.role === "admin");
	const hasSignDocumentsSubscription = hasSubscriptionFeature("SIGN_DOCUMENTS");
	const isSignable = isSignableDocument(attachment.name);
	const canSign = hasSignDocumentsSubscription && isSignable && isAdmin && !!folderId;

	if (debugContext && isDocumentFolderDebugEnabled()) {
		logFolderIdResolution(`${debugContext} → actions menu`, attachment, {
			folderId,
			canSign,
			hasSignDocumentsSubscription,
			isSignable,
			isAdmin,
			foldersListProvided: Array.isArray(foldersList) && foldersList.length > 0,
			foldersListCount: foldersList?.length ?? 0,
			fallbackStoreFolderId,
		});
	}

	const handleDownload = () => {
		downloadStoreFolderAttachment(attachment, fallbackStoreFolderId, foldersList, t);
	};

	const deleteAttachment = (fid, url, name) => {
		documentsService
			.deleteAttachment(fid, url)
			.then((response) => {
				if (!response?.ok) {
					const backendError =
						response?.errors?.[0]?.description ||
						response?.message ||
						t("documents.errorDeleteDocument");
					throw new Error(backendError);
				}
				toastr.info(t("common.success"), t("documents.fileDeleted", { name }));
				onAfterMutation?.();
			})
			.catch((error) => {
				toastr.error("Error", error?.message || t("documents.errorDeleteDocument"));
			});
	};

	const handleDelete = () => {
		const { name, url } = attachment;
		const fid = folderId;
		if (!fid) {
			toastr.error("Error", t("documents.errorDeleteMissingFolder"));
			return;
		}
		dispatchShowModal(
			{
				open: true,
				title: t("documents.deleteDocument"),
				message: t("documents.deleteDocumentConfirm", { name }),
				deleteAction: () => {
					dispatchHideModal();
					deleteAttachment(fid, url, name);
				},
				style: {
					width: "400px",
				},
				closeModal: dispatchHideModal,
			},
			"delete"
		);
	};

	const handleSign = () => {
		if (!isSignableDocument(attachment.name)) {
			toastr.error(t("documents.incompatibleFormat"), getFormatErrorMessage(attachment.name));
			return;
		}

		if (!hasSubscriptionFeature("SIGN_DOCUMENTS")) {
			setUpgradeFeature(t("documents.signWithSignaturit"));
			setShowUpgradeModal(true);
			return;
		}

		if (!folderId) {
			toastr.error("Error", t("documents.errorDeterminingFolder"));
			return;
		}

		dispatchShowModal(
			{
				open: true,
				title: t("documents.configureSigners"),
				style: { width: "800px" },
				content: (
					<DocumentSignContent
						closeModal={closeModal}
						attachment={attachment}
						storeFolderId={folderId}
						currentUser={user}
						onSignSuccess={() => onAfterMutation?.()}
					/>
				),
				closeModal,
			},
			"edit"
		);
	};

	const handleMove = () => {
		const currentFolderId = folderId;
		if (!currentFolderId) {
			toastr.error("Error", t("documents.errorMoveMissingFolder"));
			return;
		}

		dispatchShowModal(
			{
				open: true,
				title: t("documents.moveDocument"),
				style: { width: "700px" },
				content: (
					<DocumentMoveModal
						handleClose={closeModal}
						attachment={attachment}
						currentFolderId={currentFolderId}
						onMoveSuccess={() => onAfterMutation?.()}
					/>
				),
				closeModal,
			},
			"edit"
		);
	};

	const handleRename = () => {
		const currentFolderId = folderId;
		if (!currentFolderId) {
			toastr.error("Error", t("documents.errorRenameMissingFolder"));
			return;
		}

		dispatchShowModal(
			{
				open: true,
				title: t("documents.renameDocumentTitle"),
				style: { width: "500px" },
				content: (
					<DocumentRenameModal
						handleClose={closeModal}
						attachment={attachment}
						currentFolderId={currentFolderId}
						onRenameSuccess={() => onAfterMutation?.()}
					/>
				),
				closeModal,
			},
			"edit"
		);
	};

	return (
		<>
			<Dropdown className={className} align="end" drop="down">
				<Dropdown.Toggle variant="action" className="recent-document-actions-toggle">
					<FiMoreHorizontal />
				</Dropdown.Toggle>
				<Dropdown.Menu popperConfig={ACTIONS_MENU_POPPER_CONFIG} renderOnMount>
					<Dropdown.Item
						as="button"
						onClick={handleSign}
						disabled={!canSign}
						className={!canSign ? "disabled" : ""}
						style={{
							opacity: !canSign ? 0.5 : 1,
							cursor: !canSign ? "not-allowed" : "pointer",
						}}
					>
						{isHaddockEnvironment() ? t("documents.signDocument") : t("documents.signWithSignaturit")}
					</Dropdown.Item>
					<Dropdown.Item
						as="button"
						disabled={!folderId}
						className={!folderId ? "disabled" : ""}
						onClick={handleRename}
					>
						{t("documents.renameDocumentTitle")}
					</Dropdown.Item>
					<Dropdown.Item
						as="button"
						disabled={!folderId}
						className={!folderId ? "disabled" : ""}
						onClick={handleDownload}
					>
						{t("documents.download")}
					</Dropdown.Item>
					<Dropdown.Item
						as="button"
						disabled={!folderId}
						className={!folderId ? "disabled" : ""}
						onClick={handleMove}
					>
						{t("documents.move")}
					</Dropdown.Item>
					{canDelete && folderId && (
						<Dropdown.Item as="button" onClick={handleDelete}>
							{t("documents.deleteShort")}
						</Dropdown.Item>
					)}
				</Dropdown.Menu>
			</Dropdown>
			<UpgradeModal
				show={showUpgradeModal}
				onClose={() => setShowUpgradeModal(false)}
				feature={upgradeFeature}
				requiredPlan="Pro"
			/>
		</>
	);
};

const mapStateToProps = (reducers) => ({
	user: reducers.authReducer.user,
});

const mapDispatchToProps = (dispatch) => ({
	showModal: (modalProps, modalType) => dispatch(showModal({ modalProps, modalType })),
	hideModal: () => dispatch(hideModal()),
});

export default connect(mapStateToProps, mapDispatchToProps)(DocumentAttachmentActionsMenu);
